import { Amount } from '@/types/amount';
import { Account, FiatAccount, isFiatAccount } from '@/types/contacts';
import { isCryptoAccount } from '@/types/contacts.ext';
import { CurrencyCodeType, PaymentRailType } from '@/types/currency';
import { FiatAccountType } from '@/types/fiatAccountType';
import { NotNullable } from '@/types/notNullable';
import { isRecipientResponse, RecipientResponse } from '@/types/recipient';
import { FxLockType } from '@/types/simulation';
import {
  isPixInLocalInfo,
  isPixOutLocalInfo,
  isSpeiInLocalInfo,
  isSpeiOutLocalInfo,
} from '@/types/transaction.ext';

export enum StatusType {
  CREATED = 'CREATED',
  PENDING = 'PENDING',
  AWAITING_APPROVAL = 'AWAITING_APPROVAL',
  SUCCEEDED = 'SUCCEEDED',
  WAITING_WEBHOOK = 'WAITING_WEBHOOK',
  FAILED = 'FAILED',
  EXPIRED = 'EXPIRED',
  CANCELLED = 'CANCELLED',
  REJECTED = 'REJECTED',
  RETURNED = 'RETURNED',
  REFUNDED = 'REFUNDED',
  REVERSED = 'REVERSED',

  DEPOSIT_RECEIVED = 'DEPOSIT_RECEIVED',
  DEPOSIT_COMPLETED = 'DEPOSIT_COMPLETED',

  PAYMENT_UNDER_REVIEW = 'PAYMENT_UNDER_REVIEW',
  PROCESSED_BY_BANK = 'PROCESSED_BY_BANK',
  PROCESSED_BY_INTERMEDIARY_BANK = 'PROCESSED_BY_INTERMEDIARY_BANK',
  RECEIVED_BY_INTERMEDIARY_BANK = 'RECEIVED_BY_INTERMEDIARY_BANK',
  RECEIVED_BY_BENEFICIARY_BANK = 'RECEIVED_BY_BENEFICIARY_BANK',
}

export enum IntegratorOperation {
  ON_RAMP = 'ON_RAMP',
  ON_RAMP_DEPOSIT_RECEIVED = 'ON_RAMP_DEPOSIT_RECEIVED',
  OFF_RAMP = 'OFF_RAMP',
  PAYMENT_IN = 'PAYMENT_IN',
  PAYMENT_OUT = 'PAYMENT_OUT',
  BENEFICIARY_BANK_ACCOUNT_VALIDATION = 'BENEFICIARY_BANK_ACCOUNT_VALIDATION',
  JUMIO_PROXY = 'JUMIO_PROXY',
  BENEFICIARY_KYC = 'BENEFICIARY_KYC',
  BENEFICIARY_KYB = 'BENEFICIARY_KYB',
  BENEFICIARY_APPROVED = 'BENEFICIARY_APPROVED',
  PIX_DEPOSIT_RECEIVED = 'PIX_DEPOSIT_RECEIVED',
  WIRE_DEPOSIT_RECEIVED = 'WIRE_DEPOSIT_RECEIVED',
  SPEI_DEPOSIT_RECEIVED = 'SPEI_DEPOSIT_RECEIVED',
  CRYPTO_DEPOSIT_RECEIVED = 'CRYPTO_DEPOSIT_RECEIVED',
  TRANSACTION_COMPLETED = 'TRANSACTION_COMPLETED',
  TRANSACTION_FAILED = 'TRANSACTION_FAILED',
  TRANSACTION_EXPIRED = 'TRANSACTION_EXPIRED',
  TRANSACTION_SETTLED = 'TRANSACTION_SETTLED',
}

export enum BlockOperationType {
  DEPOSIT = 'DEPOSIT',
  EXCHANGE = 'EXCHANGE',
  WITHDRAWAL = 'WITHDRAWAL',
  TRANSFER = 'TRANSFER',
  INTERNAL_TRANSFER = 'EXTERNAL_TRANSFER',
  OPEN_EXCHANGE = 'OPEN_EXCHANGE',
  CLOSE_EXCHANGE = 'CLOSE_EXCHANGE',
}

export interface TransactionNotification {
  readonly status: StatusType;
  readonly operation: IntegratorOperation;
}

export interface BlockchainInfo {
  readonly depositTransactionHash: string | null;
  readonly fundingWallet: string | null;
  readonly targetWallet: string | null;
  readonly withdrawalTransactionHash: string | null;
}

export enum TransactionType {
  TRANSFER = 'TRANSFER',
  INTERNAL_TRANSFER = 'INTERNAL_TRANSFER',
  WITHDRAWAL = 'WITHDRAWAL',
  DEPOSIT = 'DEPOSIT',
  EXCHANGE = 'EXCHANGE',
  REFUND = 'REFUND',
  REVERSAL = 'REVERSAL',
  UNSET = 'UNSET',

  ALL = 'ALL',
}

export enum SimplifiedTransactionStatus {
  COMPLETED = 'COMPLETED',
  FAILED = 'FAILED',
  EXPIRED = 'EXPIRED',
  IN_PROGRESS = 'IN_PROGRESS',
  AWAITING_APPROVAL = 'AWAITING_APPROVAL',
  REJECTED = 'REJECTED',
  CREATED = 'CREATED',
  ALL = 'ALL',
}

export enum PaymentApprovalDecision {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export interface PaymentApprovalAction {
  readonly action: PaymentApprovalDecision;
  readonly actor: string;
  readonly note: string | null;
  readonly createdAt: string;
}

export interface PayInSenderDetails {
  readonly senderName?: string;
  readonly senderReference?: string;
  readonly accountNumber?: string;
  readonly routingNumber?: string;
  readonly bankName?: string;
  readonly receiverAccountNumber?: string;
  readonly receiverAccountTitle?: string;
  readonly iban?: string;
  readonly networkPlatform?: string;
}

export interface PayInSender {
  readonly paymentRails: PaymentRailType | null;
  readonly details: PayInSenderDetails | null;
}

export interface Transaction {
  readonly id: string;
  readonly simulationId?: string;
  readonly originalSimulationId?: string;
  readonly date: string;
  readonly status: StatusType;
  readonly type: TransactionType;
  readonly paymentType: FiatAccountType | PaymentRailType;
  readonly fromAccountAlias: string | null;
  readonly fromAccount: Account | null;
  readonly fromAmount: Amount;
  readonly toAccountAlias: string | null;
  readonly toAccount: Account | null;
  readonly toRecipient: RecipientResponse | null;
  readonly toAmount: Amount;
  readonly localInfo: LocalInfo;
  readonly transactionDetails: TransactionDetails;
  readonly initiatedBy: string | null;
  readonly completed: boolean;
  readonly payorId: string | null;
  readonly beneficiaryId?: string | null;
  readonly targetBeneficiaryId?: string | null;
  readonly beneficiaryName: string;
  readonly targetBeneficiaryName?: string | null;
  readonly efx: boolean;
  readonly hasAcam220File?: boolean;
  readonly payInSender?: PayInSender | null;
  readonly blockchainInfo?: BlockchainInfo;
  readonly expiresAt?: string | null;
  readonly fxLockType?: FxLockType | null;
  readonly fxLockSpreadPercentage?: number | null;
  readonly originalTransactionId?: string | null;
  readonly refundTransactionId?: string | null;
  readonly refundedAt?: string | null;
  readonly paymentApprovalLastDecision?: PaymentApprovalDecision | null;
  readonly paymentApprovalLastActor?: string | null;
  readonly paymentApprovalLastDecidedAt?: string | null;
}

export interface TransactionResponse {
  readonly id: string;
  readonly simulationId: string;
  readonly integratorId: string;
  readonly initiatedBy: string | null;
  readonly beneficiaryId: string;
  readonly from: From;
  readonly to: To;
  readonly status: string;
  readonly blockchainInfo: BlockchainInfo;
  readonly localInfo: LocalInfo;
  readonly beneficiaryIp: string;
  readonly flowOfFundsId: string;
  readonly transactionDetails: TransactionDetails;
  readonly createdDate: string;
  readonly lastModifiedDate: string;
  readonly payorId: string | null;
  readonly efx: boolean;
  readonly expiresAt?: string | null;
}

export interface From {
  readonly currencyCode: string;
  readonly value: string;
}

export interface To {
  readonly currencyCode: string;
  readonly value: string;
}

export interface SpeiInLocalInfo {
  readonly speiInClabe?: string;
  readonly speiInBankName: string;
  readonly speiInBeneficiaryName: string;
}

export interface SpeiOutLocalInfo {
  readonly speiOutClabe: string;
  readonly speiOutSearchableId: string;
}

export interface PixInLocalInfo {
  readonly pixCopyAndPaste: string;
  readonly pixExpiresAt: string;
}

export interface PixOutLocalInfo {
  readonly targetPixKey: string;
  readonly pixOutSearchableId: string;
}

export interface SwiftLocalInfo {
  readonly destinationFiatAccount?: FiatAccount;
  readonly uetr: string;
  readonly imad?: string;
  readonly omad?: string;
  readonly pacs008?: string;
}

export type LocalInfo =
  | SpeiInLocalInfo
  | SpeiOutLocalInfo
  | PixInLocalInfo
  | PixOutLocalInfo
  | SwiftLocalInfo;

export enum ExpectedSettlementTimeType {
  INSTANT_MARKET_HOURS = 'INSTANT_MARKET_HOURS',
  INSTANT = 'INSTANT',
  WITHIN_5_MINUTES = 'WITHIN_5_MINUTES',
  WITHIN_15_MINUTES = 'WITHIN_15_MINUTES',
  SAME_DAY_BEFORE_CUTOFF = 'SAME_DAY_BEFORE_CUTOFF',
  ONE_TO_TWO_DAYS = 'ONE_TO_TWO_DAYS',
  ONE_TO_THREE_DAYS = 'ONE_TO_THREE_DAYS',
  UNKNOWN = 'UNKNOWN',
}

export interface ExpectedSettlementTimeDetails {
  readonly expectedTime: ExpectedSettlementTimeType;
  readonly displayText: string;
  readonly additionalInfo?: string;
  readonly cutoffTime?: string;
}

export interface TransactionDetails {
  readonly conversionDetails?: ConversionDetails | null;
  readonly feeDetails: FeeDetails;
  readonly transferPurpose: string | null;
  readonly paymentReference: string | null;
  readonly memo: string | null;
  readonly expectedSettlementTimeDetails: ExpectedSettlementTimeDetails | null;
}

export interface ConversionDetails {
  readonly effectiveTransactionValue: number;
  readonly marketExchangeRate: number;
  readonly clientRate: number;
  readonly calizaRate: number;
  readonly integratorRate?: number;
  readonly amountToBeConverted: NotNullable<Amount>;
}

export enum FeeType {
  DEPOSIT = '',
  INTEGRATOR = 'INTEGRATOR',
  CALIZA = 'CALIZA',
  TAX = 'TAX',
  EXCHANGE = 'EXCHANGE',
  NETWORK = 'NETWORK',
  WITHDRAWAL = 'WITHDRAWAL',
  TRANSFER = 'TRANSFER',
  CONVERSION_FEE_ESTIMATED = 'CONVERSION_FEE_ESTIMATED',
  // Deprecated
  POST_BUSINESS_HOURS = 'POST_BUSINESS_HOURS',
  CALIZA_DEPOSIT = 'CALIZA_DEPOSIT',
  CALIZA_WITHDRAWAL = 'CALIZA_WITHDRAWAL',
  CALIZA_SPREAD = 'CALIZA_SPREAD',
  INTEGRATOR_SPREAD = 'INTEGRATOR_SPREAD',
  PRE_FIXED = 'PRE_FIXED',
  POST_FIXED = 'POST_FIXED',
}

export enum ChargeModeType {
  POST_PAID = 'POST_PAID',
  PRE_PAID = 'PRE_PAID',
}

export interface FixedFee {
  readonly block: BlockOperationType;
  readonly type: FeeType;
  readonly value: string;
  readonly currencyCode: CurrencyCodeType;
  readonly concept: string;
  readonly paymentRail: PaymentRailType;
  readonly chargeMode: ChargeModeType;
}

export interface PercentageFee {
  readonly block: BlockOperationType;
  readonly type: FeeType;
  readonly percentageRate: string;
  readonly baseAmount: string;
  readonly baseCurrency: CurrencyCodeType;
  readonly calculatedFee: string;
  readonly feeCurrency: CurrencyCodeType;
  readonly concept: string;
  readonly paymentRail: PaymentRailType;
  readonly chargeMode: ChargeModeType;
}

export interface SpreadFee {
  readonly block: BlockOperationType;
  readonly type: FeeType;
  readonly fromCurrency: CurrencyCodeType;
  readonly toCurrency: CurrencyCodeType;
  readonly marketRate: string;
  readonly spreadBasisPoint: string;
  readonly effectiveRate: string;
  readonly calizaRate: string;
  readonly baseCurrency: CurrencyCodeType;
  readonly baseAmount: string;
  readonly calculatedFee: string;
  readonly feeCurrency: CurrencyCodeType;
  readonly concept: string;
  readonly chargeMode: ChargeModeType;
}

export interface OldFee {
  readonly block: BlockOperationType;
  readonly type: FeeType;
  readonly fromCurrency: CurrencyCodeType;
  readonly toCurrency: CurrencyCodeType;
  readonly feeValue: string;
  readonly feeCurrency: CurrencyCodeType;
  readonly marketRate: string;
  readonly effectiveRate: string;
  readonly calizaRate: string;
  readonly baseCurrency: CurrencyCodeType;
  readonly baseAmount: string;
  readonly concept: string;
  readonly chargeMode: ChargeModeType;
}

export interface FeeDetails {
  readonly taxes: NotNullable<Amount> | null;
  readonly integratorFee: Amount;
  readonly fixedFees: ReadonlyArray<FixedFee>;
  readonly percentageFees: ReadonlyArray<PercentageFee>;
  readonly spreadFees: ReadonlyArray<SpreadFee>;
  readonly oldFees: ReadonlyArray<OldFee>;
  readonly totalFees: NotNullable<Amount>;
  readonly vet: string;
}

export interface IntegratorFee {
  readonly currencyCode: string;
  readonly value: string;
}

export enum MarketExecutionType {
  executeImmediately = 'EXECUTE_IMMEDIATELY',
  marketOpenTime = 'MARKET_OPEN_TIME',
  none = 'NONE',
}

export enum TradeType {
  GENERAL_GOODS = 'GENERAL_GOODS',
  SERVICE_TRADE = 'SERVICE_TRADE',
  DIGITAL_GOODS = 'DIGITAL_GOODS',
  ROYALTIES = 'ROYALTIES',
  LICENSING = 'LICENSING',
}

export enum ProductCategory {
  APPAREL = 'APPAREL',
  ELECTRONICS = 'ELECTRONICS',
  FOOD_AND_BEVERAGE = 'FOOD_AND_BEVERAGE',
  FURNITURE = 'FURNITURE',
  MACHINERY = 'MACHINERY',
  RAW_MATERIALS = 'RAW_MATERIALS',
  TEXTILES = 'TEXTILES',
  TOYS = 'TOYS',
  AUTOMOTIVE = 'AUTOMOTIVE',
  COSMETICS = 'COSMETICS',
  OTHER = 'OTHER',
}

export interface TradeProduct {
  readonly name: string;
  readonly quantity: string;
  readonly productCategory: ProductCategory;
}

export interface TradeCustoms {
  readonly customsDecNo: string;
}

export interface TradeLogistics {
  readonly logisticsCompany: string;
  readonly logisticsOrderNo: string;
  readonly shipDate?: string;
}

export interface TradeDocumentDetails {
  readonly tradeType: TradeType;
  readonly products: readonly TradeProduct[];
  readonly customs: TradeCustoms;
  readonly logistics: TradeLogistics;
}

export interface TransactionRequest {
  readonly simulationId: string;
  readonly beneficiaryIp: string;
  readonly marketExecutionType: MarketExecutionType;

  readonly attachments?: File[];
  readonly transferPurpose?: string;
  readonly paymentReference?: string;
  readonly memo?: string;
  readonly efx?: boolean;
  readonly achSameDay?: boolean;
  readonly tradeDocumentDetails?: TradeDocumentDetails;
}

export interface PoboPayoutRequest {
  readonly simulationId: string;
  readonly beneficiaryIp: string;
  readonly achSameDay?: boolean;
  readonly marketExecutionType: MarketExecutionType;
  readonly memo?: string;
  readonly transferPurpose?: string;
  readonly paymentReference?: string;
}

export interface PoboPayoutResponse {
  readonly id: string;
  readonly simulationId: string;
  readonly internalTransferTransactionId: string;
}

export interface TransactionDocument {
  readonly id: string;
  readonly name: string;
  readonly size: number;
  readonly mimeType?: string;
  readonly uploadDate?: string;
  readonly viewUrl?: string;
}

export interface TransactionWithDocuments extends Transaction {
  readonly documents: readonly TransactionDocument[];
}

export interface MockDepositRequest {
  readonly type: string;
  readonly transactionId: string;
  readonly documentId: string;
}

export interface TransactionEtaResponse {
  readonly etaHours: number;
}

export interface UploadEfxTransactionACAM220FileRequest {
  id: string;
  file: File;
}

export interface UploadTransactionDocumentsRequest {
  id: string;
  files: File[];
}

const expiredStatuses = [StatusType.EXPIRED];

const rejectedStatuses = [StatusType.REJECTED];

const failedStatues = [
  StatusType.FAILED,
  StatusType.CANCELLED,
  StatusType.RETURNED,
  StatusType.REFUNDED,
  StatusType.REVERSED,
];

// A reversed inbound deposit arrives with type=REFUND + status=REVERSED (it reuses the
// refund link plumbing). Surface it as its own "Reversal" type label, keyed off status.
export const transactionTypeLabelKey = (t: {
  type: TransactionType;
  status: StatusType;
}): TransactionType => (t.status === StatusType.REVERSED ? TransactionType.REVERSAL : t.type);

export const simplifyTransactionStatus = (
  status: StatusType,
  isFinal: boolean = false,
): SimplifiedTransactionStatus => {
  if (status === StatusType.AWAITING_APPROVAL) {
    return SimplifiedTransactionStatus.AWAITING_APPROVAL;
  }

  if (expiredStatuses.includes(status)) {
    return SimplifiedTransactionStatus.EXPIRED;
  }

  if (rejectedStatuses.includes(status)) {
    return SimplifiedTransactionStatus.REJECTED;
  }

  if (failedStatues.includes(status)) {
    return SimplifiedTransactionStatus.FAILED;
  }

  if (status.includes('SUCCEEDED') || isFinal) {
    return SimplifiedTransactionStatus.COMPLETED;
  }

  return SimplifiedTransactionStatus.IN_PROGRESS;
};

export const isAwaitingFunding = (transaction: Transaction): boolean => {
  if (transaction.status !== StatusType.CREATED) {
    return false;
  }
  if (!transaction.fxLockType || transaction.fxLockType === FxLockType.DEFAULT) {
    return false;
  }
  if (!transaction.expiresAt) {
    return false;
  }
  return new Date(transaction.expiresAt).getTime() > Date.now();
};

export const isLocalInfo = (something: unknown): something is LocalInfo =>
  isPixInLocalInfo(something) ||
  isSpeiInLocalInfo(something) ||
  isPixOutLocalInfo(something) ||
  isSpeiOutLocalInfo(something);

export { isRecipientResponse } from '@/types/recipient';

export const isAccount = (something: unknown): something is Account =>
  isFiatAccount(something) || isCryptoAccount(something);

export const isAccountOrRecipient = (
  something: unknown,
): something is Account | RecipientResponse =>
  isAccount(something) || isRecipientResponse(something);

export const isAccountOrNull = (something: unknown): something is Account | null =>
  something === null || isAccount(something);
